import { Link } from 'react-router-dom';
import {
  ASSETS, PATHS, BRAND, PROGRAMME_STRUCTURE, THERAPEUTIC_PRINCIPLES, WHO_FOR, APPROACHES,
} from '../lib/content';
import { Leaf, Sparkles, Heart } from 'lucide-react';

export default function About() {
  return (
    <div data-testid="page-about">
      {/* INTRO */}
      <section className="pt-28 md:pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-5">About</p>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-brown leading-[1.05]">
            What is <em className="italic text-brand-brown-light">Nurture the Self?</em>
          </h1>
          <p className="mt-5 font-serif italic text-xl text-brand-gold tracking-wide">
            {BRAND.strapline}
          </p>
          <p className="mt-7 font-sans text-base md:text-lg text-brand-brown-light/90 leading-relaxed">
            Nurture the Self is a therapeutic pathway that recognises difficulties with food are rarely just about food itself. It is a year-long journey — three interconnected 12-week Paths — held in small, carefully composed groups.
          </p>
          <p className="mt-5 font-serif italic text-lg text-brand-brown-light leading-relaxed">
            {BRAND.centralMessage}
          </p>
        </div>
      </section>

      {/* INTERSECTIONS */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="order-2 md:order-1">
              <Leaf className="text-brand-gold mb-5" size={20} />
              <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">
                Eating difficulties live at an intersection.
              </h2>
              <p className="mt-6 font-sans text-base text-brand-brown-light/90 leading-relaxed">
                Difficulties with food and body often emerge where many threads of a life meet — and so the way back is rarely a single fix. We hold space for what lies beneath:
              </p>
              <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 font-sans text-sm text-brand-brown-light">
                {[
                  'life experiences', 'relationships', 'attachment patterns',
                  'perfectionism', 'emotional regulation', 'self-worth',
                  'identity', 'stress and overwhelm', 'disconnection from the body',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-7 font-sans text-base text-brand-brown-light/90 leading-relaxed">
                Recovery is not simply the absence of symptoms but a gradual returning to oneself. The work is non-pathologising, trauma-informed, relational, and whole-person centred.
              </p>
            </div>
            <div className="order-1 md:order-2 relative">
              <div className="absolute -inset-3 bg-brand-sage rounded-3xl rotate-1" aria-hidden />
              <img src={ASSETS.goldField} alt="A wildflower meadow — many threads meeting in warm light" className="relative rounded-3xl object-cover w-full aspect-square" />
            </div>
          </div>
        </div>
      </section>

      {/* THREE PATHS — DEVELOPMENTAL ARC */}
      <section className="py-24 bg-brand-bg-soft">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">The developmental arc</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-brown">
              Nourish · Reconnect · Become
            </h2>
            <p className="mt-5 font-sans text-base text-brand-brown-light max-w-2xl mx-auto leading-relaxed">
              Three interconnected 12-week Paths form one year-long therapeutic journey. Each Path is complete in itself, and each prepares the ground for the next.
            </p>
          </div>

          <div className="space-y-6">
            {PATHS.map((p, i) => (
              <div
                key={p.id}
                data-testid={`about-path-${i + 1}`}
                className="bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5"
              >
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-3">{p.label}</p>
                    <h3 className="font-serif text-2xl md:text-3xl text-brand-brown leading-snug">{p.title}</h3>
                    <p className="mt-3 font-sans text-xs uppercase tracking-[0.22em] text-brand-brown-muted">
                      Exploring · {p.framework}
                    </p>
                  </div>
                  <div className="md:col-span-2 space-y-5">
                    <div>
                      <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted mb-2">Core question</p>
                      <p className="font-serif italic text-xl text-brand-brown-light leading-snug">{p.core_question}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {p.themes.map((t) => (
                        <span key={t} className="text-sm font-sans px-3.5 py-1.5 rounded-full bg-brand-sage text-brand-brown">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/courses"
              data-testid="about-cta-paths"
              className="inline-flex font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-7 py-3 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
            >
              See the Paths in detail
            </Link>
          </div>
        </div>
      </section>

      {/* WHO FOR */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-16">
          <div>
            <Sparkles className="text-brand-gold mb-5" size={20} />
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">Who is this for?</h2>
            <p className="mt-5 font-sans text-base text-brand-brown-light/90 leading-relaxed">
              Nurture the Self is for adults walking with any of the experiences below. A formal diagnosis is not required — only the willingness to gently turn toward your own life with curiosity and care.
            </p>
            <ul className="mt-7 space-y-3 font-sans text-base text-brand-brown-light/90">
              {WHO_FOR.map((w) => (
                <li key={w} className="flex items-start gap-3" data-testid={`about-who-${w.split(' ')[0]}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-sage rounded-3xl p-8 md:p-10">
            <Heart className="text-brand-gold mb-5" size={20} />
            <h3 className="font-serif text-3xl text-brand-brown leading-tight">Our way of working</h3>
            <p className="mt-4 font-sans text-base text-brand-brown-light/90 leading-relaxed">
              The pathway integrates many traditions of human understanding — woven together, gently, around your own pace.
            </p>
            <ul className="mt-6 space-y-2.5 font-sans text-sm text-brand-brown-light/90">
              {APPROACHES.map((a) => (
                <li key={a} className="flex items-start gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-brown/40 mt-2 shrink-0" />
                  <span className="capitalize">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW THE PROGRAMME WORKS */}
      <section className="py-24 bg-brand-bg-soft">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Programme structure</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-brown">How the programme works</h2>
            <p className="mt-5 font-sans text-base text-brand-brown-light max-w-2xl mx-auto leading-relaxed">
              Each 12-week Path follows the same considered shape, with rhythm and integration built in.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROGRAMME_STRUCTURE.map((s, i) => (
              <div key={i} data-testid={`about-structure-${i}`} className="bg-brand-bone rounded-3xl p-7 border border-brand-brown/5">
                <div className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-3">0{i + 1}</div>
                <h3 className="font-serif text-2xl text-brand-brown mb-3 leading-snug">{s.title}</h3>
                <p className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THERAPEUTIC PHILOSOPHY */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Therapeutic philosophy</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-brown leading-tight">What we believe</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-y-5 gap-x-10">
            {THERAPEUTIC_PRINCIPLES.map((p, i) => (
              <li key={i} className="flex items-start gap-4 font-serif text-xl text-brand-brown-light leading-snug" data-testid={`about-belief-${i}`}>
                <span className="font-serif italic text-brand-gold text-2xl mt-0.5 leading-none">·</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="relative h-[320px] md:h-[440px] rounded-3xl overflow-hidden">
            <img src={ASSETS.sunThroughTrees} alt="Soft sunlight filtering through trees in a quiet meadow" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CLOSING INVITATION */}
      <section className="py-20 bg-brand-sage">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-serif italic text-2xl md:text-3xl text-brand-brown-light leading-relaxed">
            “Safe enough to explore. Structured enough to feel held. Spacious enough to unfold in one’s own time.”
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/courses" data-testid="about-cta-explore" className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500">
              Explore the Paths
            </Link>
            <Link to="/booking" data-testid="about-cta-booking" className="font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-8 py-3.5 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500">
              Begin with an intake conversation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
